import { Home, Search, SearchOff } from '@mui/icons-material';
import { Box, Button, Container, Typography } from '@mui/material';
import { theme } from '@shared/theme';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import SEO from './SEO';

const StyledContainer = styled(Container)`
  min-height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing(4)};
`;

const ContentWrapper = styled(motion.div)`
  text-align: center;
  max-width: 600px;
`;

const IconWrapper = styled(motion.div)`
  width: 160px;
  height: 160px;
  margin: 0 auto ${theme.spacing(4)};
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(192, 90, 58, 0.1) 0%, rgba(212, 130, 94, 0.05) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 50%;
    background: linear-gradient(135deg, #c05a3a 0%, #d4825e 100%);
    opacity: 0.2;
    z-index: -1;
  }
`;

const StyledSearchOffIcon = styled(SearchOff)`
  font-size: 80px !important;
  color: ${theme.palette.primary.main};
  opacity: 0.9;
`;

const Title = styled(Typography)`
  && {
    font-weight: 700;
    font-size: 2.5rem;
    color: ${theme.palette.text.primary};
    margin-bottom: ${theme.spacing(2)};
    background: linear-gradient(135deg, #1a1a1a 0%, #4a4a4a 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const Subtitle = styled(Typography)`
  && {
    font-size: 1.125rem;
    color: ${theme.palette.text.secondary};
    margin-bottom: ${theme.spacing(4)};
    line-height: 1.6;
  }
`;

const ButtonGroup = styled(Box)`
  display: flex;
  gap: ${theme.spacing(2)};
  justify-content: center;
  flex-wrap: wrap;
  margin-top: ${theme.spacing(4)};
`;

const PrimaryButton = styled(Button)`
  && {
    background: linear-gradient(135deg, #c05a3a 0%, #d4825e 100%);
    color: white;
    text-transform: none;
    font-weight: 600;
    padding: ${theme.spacing(1.5, 4)};
    border-radius: ${typeof theme.shape.borderRadius === 'number' ? theme.shape.borderRadius * 2 : 8}px;
    box-shadow: 0 4px 12px rgba(192, 90, 58, 0.25);
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 6px 20px rgba(192, 90, 58, 0.35);
      transform: translateY(-2px);
      background: linear-gradient(135deg, #3461c4 0%, #5b9aeb 100%);
    }
  }
`;

const SecondaryButton = styled(Button)`
  && {
    border: 2px solid ${theme.palette.divider};
    color: ${theme.palette.text.primary};
    text-transform: none;
    font-weight: 600;
    padding: ${theme.spacing(1.5, 4)};
    border-radius: ${typeof theme.shape.borderRadius === 'number' ? theme.shape.borderRadius * 2 : 8}px;
    transition: all 0.3s ease;
    background: ${theme.palette.background.paper};

    &:hover {
      border-color: ${theme.palette.primary.main};
      color: ${theme.palette.primary.main};
      background: rgba(192, 90, 58, 0.05);
      transform: translateY(-2px);
    }
  }
`;

const DecorativeCircle = styled(motion.div)<{ top?: string; left?: string; size?: string }>`
  position: absolute;
  width: ${(props) => props.size || '300px'};
  height: ${(props) => props.size || '300px'};
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(192, 90, 58, 0.03) 0%, rgba(212, 130, 94, 0.03) 100%);
  top: ${(props) => props.top || 'auto'};
  left: ${(props) => props.left || 'auto'};
  z-index: -1;
  pointer-events: none;
`;

function NotFoundPage() {
  const navigate = useNavigate();

  return (
    <>
      <SEO
        title="Page Not Found"
        description="The page you're looking for couldn't be found. Navigate back to the Kandir platform to continue accessing sales intelligence and deal retros."
      />
      <StyledContainer maxWidth="lg">
        <DecorativeCircle
          top="10%"
          left="5%"
          size="400px"
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Number.POSITIVE_INFINITY,
            ease: 'easeInOut',
          }}
        />
        <DecorativeCircle
          top="60%"
          left="70%"
          size="250px"
          animate={{
            scale: [1, 1.15, 1],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 6,
            repeat: Number.POSITIVE_INFINITY,
            ease: 'easeInOut',
            delay: 1,
          }}
        />

        <ContentWrapper
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <IconWrapper
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{
              type: 'spring',
              stiffness: 200,
              damping: 15,
              delay: 0.2,
            }}
          >
            <StyledSearchOffIcon />
          </IconWrapper>

          <Title variant="h1">Page Not Found</Title>

          <Subtitle variant="body1">
            We couldn&apos;t find the page you&apos;re looking for. The page may have been moved, deleted, or the URL
            might be incorrect.
          </Subtitle>

          <Box
            sx={{
              background: 'rgba(192, 90, 58, 0.05)',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              padding: theme.spacing(2),
              marginBottom: theme.spacing(2),
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              💡 Double-check the URL or navigate back to explore Kandir
            </Typography>
          </Box>

          <ButtonGroup>
            <PrimaryButton variant="contained" startIcon={<Home />} onClick={() => navigate('/my-reviews')}>
              Go to Home
            </PrimaryButton>

            <SecondaryButton variant="outlined" startIcon={<Search />} onClick={() => navigate('/company-search')}>
              Browse Companies
            </SecondaryButton>
          </ButtonGroup>
        </ContentWrapper>
      </StyledContainer>
    </>
  );
}

export default NotFoundPage;
